import { get } from 'lodash-es';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { StatesModel } from 'client/data/models/states';
import { getPageLinksPath, LinkWidgetsModel } from 'client/data/models/link-widgets';
import { pageDefinition } from 'client/site-modules/shared/pages/page';
import { getFAQSchema } from 'client/seo/schemas/faq';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { makeDDPCityStateNiceName } from 'site-modules/shared/utils/nice-name';
import { getModelState } from 'client/data/luckdragon/model';
import { getResearchArticleBreadcrumbs } from 'site-modules/editorial/utils/breadcrumbs';
import { EdmundsNoAdsDecorator } from 'site-modules/shared/pages/decorators/edmunds-no-ads';
import { getSellMyCarInStateFaqContent } from 'client/site-modules/editorial/utils/sell-car';
import { adjustStateName } from 'client/site-modules/shared/utils/adjust-state-name';
import { UrlModel, URL_CONTEXT_PATH } from 'client/data/models/url';
import { CmsModel } from 'client/data/models/cms';
import { SELL_CAR_CONTENT_PATH } from 'client/data/constants/contents-paths';
import { setCriticalCss } from 'client/actions/critical-css';
import { getIsEmoSearchEnabled } from 'client/site-modules/shared/utils/multi-offer/multi-offer';
import { getInitialTabIdFromLocation } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/utils/get-initial-tab-id-from-location';
import { REVIEWS_MODULE_USER_REVIEW_CONTENT_PATH } from 'site-modules/shared/components/offers-reviews-module/constants';

import { SellMyCarInState } from './sell-my-car-in-state';

function runPreload(store, { isPreloadAboveFold = false } = {}) {
  const state = store.getState();
  const {
    pageContext: { location, page },
  } = state;
  const preloader = new ModelPreloader(store);
  preloader.resolve('allStates', StatesModel);
  preloader.resolve('allStateCodes', StatesModel);
  preloader.set(URL_CONTEXT_PATH, UrlModel, {
    urlPattern: 'sell-car/sell-my-car-in-state',
  });

  preloader.resolve(`content["${SELL_CAR_CONTENT_PATH}"]`, CmsModel);
  preloader.resolve(getPageLinksPath(), LinkWidgetsModel);
  if (!isPreloadAboveFold) {
    preloader.resolve(`content["${REVIEWS_MODULE_USER_REVIEW_CONTENT_PATH}"]`, CmsModel);
  }

  const isEmoSearchEnabled = getIsEmoSearchEnabled(location);

  return preloader.load().then(() => {
    const initialTabId = getInitialTabIdFromLocation({
      location,
      isLicensePlateTabDefault: true,
      isSellCar: true,
      referer: page?.referer,
    });

    store.dispatch(
      setCriticalCss({
        isEmoSearchEnabled, // 2 values
        initialTabId, // 3 values
      })
    );
  });
}

export const pageDefinitionConfiguration = {
  name: 'advice_sell_article_index',
  category: 'advice',
  component: SellMyCarInState,
  decorator: EdmundsNoAdsDecorator,
  preload: store => runPreload(store),
  preloadAboveFold: store => runPreload(store, { isPreloadAboveFold: true }),
  seo: (state, props) => {
    const modelState = getModelState(state);
    const statesList = modelState.get('allStates', StatesModel) || [];
    const urlState = get(props, 'match.params.state', '');
    const urlStateNiceName = makeDDPCityStateNiceName(urlState);
    const stateName = get(
      statesList.find(({ niceName }) => niceName === urlStateNiceName),
      'name',
      ''
    );
    const title = `Sell My Car In ${adjustStateName({ stateName, isTitleCase: true })} Online For Cash`;
    const pathname = get(props, 'location.pathname', '');

    return {
      title: `${title} | Edmunds`,
      description: `Sell my car, truck, or SUV in ${adjustStateName({
        stateName,
      })} instantly online for cash with Edmunds. Free appraisal, instant no-obligation offer. Check it out. You've got nothing to lose.`,
      jsonld: [
        getFAQSchema({
          entries: getSellMyCarInStateFaqContent(stateName),
          generateLearnMoreLinks: true,
          pathname,
        }),
        getBreadcrumbSchema(getResearchArticleBreadcrumbs(['sell-car'], title, pathname)),
      ],
    };
  },
};

export default pageDefinition(pageDefinitionConfiguration);
